import { createFileRoute, Link } from "@tanstack/react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useServerFn } from "@tanstack/react-start";
import { useId, useMemo, useRef, useState } from "react";
import { AlertTriangle, ArrowLeft, CheckCircle2, Loader2, Lock, Award, BookOpen, FileQuestion, Download } from "lucide-react";
import { toast } from "sonner";
import {
  enrolInCourse,
  getCourseForLearner,
  submitFinalExam,
  submitModuleQuiz,
} from "@/lib/api/academy.functions";
import { getMyCertificates } from "@/lib/api/certificates.functions";
import { downloadCertificatePdf } from "@/lib/certificate-pdf";
import { generateFinalCertificatePdf } from "@/lib/api/certificate-generate.functions";
import { downloadBase64File } from "@/lib/download-base64";
import { AssignmentSection, PracticeSection, ScenarioSection } from "@/components/academy/AcademyAiSections";
import { CertificatePreview } from "@/components/academy/CertificatePreview";
import { AccessibleQuiz } from "@/components/academy/AccessibleQuiz";
import { supabase } from "@/integrations/supabase/client";
import { useEffect } from "react";



export const Route = createFileRoute("/_authenticated/portal/academy/$slug")({
  component: CoursePlayer,
});

type Q = {
  id: string;
  module_id: string | null;
  kind: "quiz" | "final";
  order_index: number;
  prompt: string;
  options: string[];
};

function CoursePlayer() {
  const { slug } = Route.useParams();
  const qc = useQueryClient();
  const fetchCourse = useServerFn(getCourseForLearner);
  const enrol = useServerFn(enrolInCourse);
  const submitQuiz = useServerFn(submitModuleQuiz);
  const submitFinal = useServerFn(submitFinalExam);

  const { data, isLoading } = useQuery({
    queryKey: ["academy-course", slug],
    queryFn: () => fetchCourse({ data: { slug } }),
  });

  const enrolMut = useMutation({
    mutationFn: () => enrol({ data: { slug } }),
    onSuccess: (r: any) => {
      if (r?.wasNew) toast.success("Enrolled — contract emailed. Welcome aboard.");
      qc.invalidateQueries({ queryKey: ["academy-course", slug] });
      qc.invalidateQueries({ queryKey: ["my-enrollments"] });
    },
    onError: (e: any) => toast.error(e?.message ?? "Could not enrol"),
  });

  // Live recipient name for the preview (from profile) — hooks must run on every render
  const [recipientName, setRecipientName] = useState<string>("Learner");
  const [studentNumber, setStudentNumber] = useState<string>("");
  useEffect(() => {
    (async () => {
      const { data: u } = await supabase.auth.getUser();
      const uid = u.user?.id;
      const name =
        (u.user?.user_metadata as any)?.full_name ||
        (u.user?.user_metadata as any)?.name ||
        u.user?.email?.split("@")[0] ||
        "Learner";
      setRecipientName(name);
      if (uid) {
        const { data: p } = await supabase.from("profiles").select("student_number").eq("id", uid).maybeSingle();
        if (p?.student_number) setStudentNumber(p.student_number);
      }
    })();
  }, []);

  if (isLoading || !data) {
    return <div className="p-8 text-center text-muted-foreground">Loading course…</div>;
  }

  const { course, modules, questions, progress, attempts } = data as any;
  const isEnrolled = !!progress;
  const passedMods: string[] = Array.isArray(progress?.passed_modules)
    ? progress.passed_modules
    : [];
  const status = progress?.status ?? null;

  // Progress + "current module" — first not-passed module (or last one if all done)
  const totalModules = modules.length;
  const passedCount = modules.filter((m: any) => passedMods.includes(m.id)).length;
  const progressPct = totalModules ? Math.round((passedCount / totalModules) * 100) : 0;
  const currentModule =
    modules.find((m: any) => !passedMods.includes(m.id)) || modules[modules.length - 1];
  const remainingCount = Math.max(0, totalModules - passedCount);
  const lastAttempt = attempts[0];
  const lastActivity = lastAttempt?.attempted_at
    ? new Date(lastAttempt.attempted_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })
    : progress?.contract_accepted_at
      ? new Date(progress.contract_accepted_at).toLocaleString("en-ZA", { dateStyle: "medium", timeStyle: "short" })
      : null;



  return (
    <section className="space-y-6">
      <Link
        to="/portal/academy"
        className="inline-flex items-center gap-1 text-xs text-muted-foreground hover:text-navy"
      >
        <ArrowLeft className="h-3 w-3" /> All courses
      </Link>

      <header className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="text-xs uppercase tracking-[0.3em] text-gold">
            {course.code} · {course.category}
          </p>
          <h1 className="mt-1 font-display text-2xl text-navy">{course.title}</h1>
          <p className="mt-2 max-w-3xl text-sm text-muted-foreground">{course.summary}</p>
          <div className="mt-3 flex flex-wrap gap-2 text-[11px]">
            {course.difficulty && (
              <span className="rounded-full border border-navy/20 bg-white px-2.5 py-1 font-semibold text-navy">
                {course.difficulty}
              </span>
            )}
            {course.estimated_hours != null && (
              <span className="rounded-full border border-navy/20 bg-white px-2.5 py-1 font-semibold text-navy">
                ~{course.estimated_hours} hours
              </span>
            )}
            <span className="rounded-full border border-navy/20 bg-white px-2.5 py-1 font-semibold text-navy">
              Pass mark {course.pass_threshold ?? 60}%
            </span>
          </div>
        </div>
        {studentNumber && (
          <span className="rounded-full border border-navy/30 bg-navy/5 px-3 py-1 text-[11px] font-semibold text-navy">
            Student № {studentNumber}
          </span>
        )}
      </header>

      {course.welcome_md && (
        <section className="rounded-lg border-2 border-navy/20 bg-gradient-to-br from-navy/5 to-amber-50/40 p-5">
          <p className="text-[10px] font-semibold uppercase tracking-widest text-gold">
            Welcome page · read first
          </p>
          <article className="prose prose-sm mt-2 max-w-none whitespace-pre-wrap text-sm text-foreground">
            {course.welcome_md}
          </article>
        </section>
      )}



      {course.disclaimer && (
        <div
          role="note"
          aria-labelledby="course-disclaimer-title"
          className="rounded-lg border-2 border-amber-400 bg-amber-50 p-4"
        >
          <p
            id="course-disclaimer-title"
            className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-amber-900"
          >
            <AlertTriangle className="h-4 w-4" aria-hidden="true" />
            Important — please read
          </p>
          <p className="mt-1.5 text-sm text-amber-900">{course.disclaimer}</p>
        </div>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {Array.isArray(course.learning_outcomes) && course.learning_outcomes.length > 0 && (
          <div className="rounded-lg border border-input bg-card p-4">
            <p className="text-[10px] uppercase tracking-widest text-gold">Learning outcomes</p>
            <p className="mt-1 text-xs text-muted-foreground">By the end of this course you will be able to:</p>
            <ul className="mt-2 space-y-1 text-sm text-foreground">
              {(course.learning_outcomes as string[]).map((o, i) => (
                <li key={i} className="flex items-start gap-2">
                  <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-600" aria-hidden="true" />
                  <span>{o}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        <div className="rounded-lg border border-input bg-card p-4 space-y-3">
          {course.target_learner && (
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold">Who this is for</p>
              <p className="mt-1 text-sm text-foreground">{course.target_learner}</p>
            </div>
          )}
          <div>
            <p className="text-[10px] uppercase tracking-widest text-gold">How each lesson is structured</p>
            <ul className="mt-1 space-y-0.5 text-xs text-muted-foreground">
              <li>1. Plain-language lesson with clear headings</li>
              <li>2. Glossary of key legal terms</li>
              <li>3. Worked scenario and learner activity</li>
              <li>4. Summary checklist you can print</li>
              <li>5. Short quiz to confirm understanding (pass 60%)</li>
            </ul>
          </div>
        </div>
      </div>



      {!isEnrolled && (
        <div className="rounded-lg border border-navy/30 bg-navy/5 p-4">
          <p className="text-sm text-navy">
            Enrol to access the lessons, quizzes and final exam. You'll be
            emailed the NDLCS Academy enrolment contract.
          </p>
          <button
            onClick={() => enrolMut.mutate()}
            disabled={enrolMut.isPending}
            className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-navy px-4 py-2 text-sm font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            {enrolMut.isPending && <Loader2 className="h-3 w-3 animate-spin" />}
            Enrol — free
          </button>
        </div>
      )}

      {isEnrolled && status !== "certified" && (
        <section className="rounded-lg border border-navy/20 bg-card p-4">
          <div className="flex flex-wrap items-center justify-between gap-3">
            <div>
              <p className="text-[10px] uppercase tracking-widest text-gold">Your progress</p>
              <p className="font-display text-lg text-navy">
                {passedCount} of {totalModules} module{totalModules === 1 ? "" : "s"} passed
                <span className="ml-2 text-sm font-normal text-muted-foreground">· {progressPct}%</span>
              </p>
              {lastActivity && (
                <p className="mt-0.5 text-[11px] text-muted-foreground">Last activity: {lastActivity}</p>
              )}
            </div>
            {currentModule && remainingCount > 0 && status === "in_progress" && (
              <a
                href={`#module-${currentModule.id}`}
                className="inline-flex items-center gap-2 rounded-md bg-gold px-4 py-2 text-sm font-semibold text-navy hover:opacity-90"
              >
                <BookOpen className="h-4 w-4" />
                Continue learning · Module {currentModule.order_index}
              </a>
            )}
          </div>
          <div className="mt-3 h-2 w-full overflow-hidden rounded-full bg-navy/10">
            <div
              className="h-full rounded-full bg-gradient-to-r from-navy to-gold transition-all"
              style={{ width: `${progressPct}%` }}
            />
          </div>
          <div className="mt-3 grid grid-cols-2 gap-2 text-[11px] sm:grid-cols-4">
            <span className="rounded-md border border-input bg-white px-2 py-1.5 text-center">
              <span className="block text-muted-foreground">Current</span>
              <span className="font-semibold text-navy">
                {currentModule ? `Module ${currentModule.order_index}` : "—"}
              </span>
            </span>
            <span className="rounded-md border border-input bg-white px-2 py-1.5 text-center">
              <span className="block text-muted-foreground">Remaining</span>
              <span className="font-semibold text-navy">{remainingCount}</span>
            </span>
            <span className="rounded-md border border-input bg-white px-2 py-1.5 text-center">
              <span className="block text-muted-foreground">Final exam</span>
              <span className="font-semibold text-navy">
                {remainingCount === 0
                  ? status === "awaiting_review" || status === "certified"
                    ? "Submitted"
                    : "Unlocked"
                  : "Locked"}
              </span>
            </span>
            <span className="rounded-md border border-input bg-white px-2 py-1.5 text-center">
              <span className="block text-muted-foreground">Certificate</span>
              <span className="font-semibold text-navy">
                {status === "certified"
                  ? "Issued"
                  : status === "awaiting_review"
                    ? "Awaiting review"
                    : "Not yet eligible"}
              </span>
            </span>
          </div>
        </section>
      )}



      {status === "awaiting_review" && (
        <StatusBanner
          tone="info"
          icon={<Loader2 className="h-4 w-4" />}
          title="Pending admin review"
          body="You passed every quiz and the final exam. An NDLCS administrator will issue your signed, tamper-evident certificate shortly. You'll be able to download it from here as soon as it's issued."
        />
      )}



      {status === "certified" && (
        <IssuedCertificateCard courseCode={course.code} learnerName={recipientName} />
      )}

      {status === "rejected" && (
        <StatusBanner
          tone="warn"
          icon={<Lock className="h-4 w-4" />}
          title="Submission rejected"
          body={progress?.reviewer_note || "An NDLCS admin declined certification. Contact us for details."}
        />
      )}

      {isEnrolled && (
        <>
          {status !== "certified" && (
            <section
              aria-labelledby="cert-preview-heading"
              className="rounded-lg border border-input bg-card p-4"
            >
              <div className="mb-3 flex flex-wrap items-start justify-between gap-3">
                <div>
                  <p className="text-[10px] uppercase tracking-widest text-gold">Your certificate · preview</p>
                  <h2 id="cert-preview-heading" className="text-sm font-semibold text-navy">
                    This is the exact template you'll receive once an NDLCS admin signs off your final exam.
                  </h2>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Every issued certificate carries a unique verification code, HMAC-SHA256 signature,
                    and a SHA-256 document hash so anyone can confirm it has not been altered.
                  </p>
                </div>
                <span className="rounded-full bg-amber-100 px-3 py-1 text-[10px] font-semibold uppercase tracking-widest text-amber-900">
                  Not yet issued
                </span>
              </div>
              <div
                role="img"
                aria-label={`Certificate preview for ${recipientName}, course ${course.code} — ${course.title}. Marked PREVIEW — not yet issued.`}
              >
                <CertificatePreview
                  recipientName={recipientName}
                  title={`${course.code} — ${course.title}`}
                  trackLabel="NDLCS Academy"
                  template="classic"
                  preview
                />
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button
                  type="button"
                  onClick={async () => {
                    try {
                      await downloadCertificatePdf({
                        verificationCode: "PREVIEW-ONLY",
                        recipientName,
                        trackLabel: "NDLCS Academy",
                        title: `${course.code} — ${course.title}`,
                        issuedAt: new Date().toISOString(),
                        trackRef: course.code,
                        template: "classic",
                        preview: true,
                      });
                    } catch (err: any) {
                      toast.error(err?.message ?? "Could not generate preview PDF");
                    }
                  }}
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-navy/30 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
                >
                  <Download className="h-3 w-3" aria-hidden="true" /> Download preview PDF
                </button>
                <p className="text-[11px] text-muted-foreground">
                  The preview is watermarked and marked <span className="font-semibold">PREVIEW-ONLY</span> —
                  it cannot be verified and is not a valid credential.
                </p>
              </div>
            </section>
          )}



          <ol className="space-y-4">
            {modules.map((m: any, idx: number) => (
              <ModuleBlock
                key={m.id}
                index={idx + 1}
                slug={slug}
                module={m}
                questions={questions.filter((q: Q) => q.module_id === m.id && q.kind === "quiz")}
                isPassed={passedMods.includes(m.id)}
                attempts={attempts.filter((a: any) => a.module_id === m.id)}
                onSubmit={async (payload: { responses: { questionId: string; selectedIndex: number }[]; elapsedSeconds: number }) => {
                  const r = await submitQuiz({ data: { courseSlug: slug, moduleId: m.id, ...payload } });
                  if (r.passed) toast.success(`Quiz passed (${r.pct}%)`);
                  else toast.error(`Scored ${r.pct}% — need ${r.threshold}%. Review feedback and try again.`);
                  qc.invalidateQueries({ queryKey: ["academy-course", slug] });
                  return r;
                }}
              />
            ))}
          </ol>

          <FinalExamBlock
            slug={slug}
            unlocked={modules.length > 0 && modules.every((m: any) => passedMods.includes(m.id))}
            alreadyPassed={status === "awaiting_review" || status === "certified"}
            questions={questions.filter((q: Q) => q.kind === "final")}
            attempts={attempts.filter((a: any) => a.kind === "final")}
            onSubmit={async (payload: { responses: { questionId: string; selectedIndex: number }[]; elapsedSeconds: number }) => {
              const r = await submitFinal({ data: { courseSlug: slug, ...payload } });
              if (r.passed) toast.success(`Final exam passed (${r.pct}%) — awaiting admin review.`);
              else toast.error(`Scored ${r.pct}% — need ${r.threshold}%. Review feedback and try again.`);
              qc.invalidateQueries({ queryKey: ["academy-course", slug] });
              return r;
            }}
          />

          <ScenarioSection slug={slug} />
          <AssignmentSection slug={slug} />
          <PracticeSection slug={slug} />

        </>
      )}
    </section>
  );
}

function StatusBanner({
  tone, icon, title, body,
}: { tone: "info" | "success" | "warn"; icon: React.ReactNode; title: string; body: string }) {
  const map = {
    info: "border-sky-300 bg-sky-50 text-sky-900",
    success: "border-emerald-300 bg-emerald-50 text-emerald-900",
    warn: "border-amber-300 bg-amber-50 text-amber-900",
  };
  return (
    <div className={`rounded-md border px-4 py-3 ${map[tone]}`}>
      <p className="flex items-center gap-2 text-sm font-semibold">{icon} {title}</p>
      <p className="mt-1 text-xs">{body}</p>
    </div>
  );
}

function shuffleArr<T>(arr: T[]): T[] {
  const out = [...arr];
  for (let i = out.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [out[i], out[j]] = [out[j], out[i]];
  }
  return out;
}



function ModuleBlock({
  index, slug, module: m, questions, isPassed, attempts, onSubmit,
}: any) {
  const [open, setOpen] = useState(!isPassed);
  const [shuffled, setShuffled] = useState<Q[]>(() => shuffleArr<Q>(questions));
  const lastAttempt = attempts[0];
  const panelId = `module-panel-${m.id}`;
  const headingId = `module-heading-${m.id}`;

  // Build a summary checklist from key_points (fallback) so every lesson has one.
  const checklist: string[] = Array.isArray(m.summary_checklist) && m.summary_checklist.length
    ? m.summary_checklist
    : Array.isArray(m.key_points) ? m.key_points : [];
  // Optional glossary — only render when present in module data.
  const glossary: Array<{ term: string; definition: string }> = Array.isArray(m.glossary)
    ? m.glossary.filter((g: any) => g && g.term && g.definition)
    : [];

  return (
    <li id={`module-${m.id}`} className="scroll-mt-24 rounded-lg border border-input bg-card">
      <h3 id={headingId} className="m-0">
        <button
          type="button"
          onClick={() => setOpen((o) => !o)}
          aria-expanded={open}
          aria-controls={panelId}
          className="flex w-full items-center justify-between gap-2 px-4 py-3 text-left focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
        >
          <span className="flex items-center gap-3">
            <span className="grid h-7 w-7 place-items-center rounded-full bg-navy text-xs font-bold text-navy-foreground" aria-hidden="true">
              {index}
            </span>
            <span>
              <span className="block text-[10px] uppercase tracking-widest text-gold">
                Module {m.order_index}
                {isPassed ? " · passed" : ""}
              </span>
              <span className="block font-display text-base text-navy">{m.title}</span>
            </span>
          </span>
          {isPassed
            ? <CheckCircle2 className="h-5 w-5 text-emerald-600" aria-label="Passed" />
            : <BookOpen className="h-5 w-5 text-muted-foreground" aria-hidden="true" />}
        </button>
      </h3>

      {open && (
        <div id={panelId} role="region" aria-labelledby={headingId} className="border-t border-border px-4 py-4">
          <h4 className="mb-1 text-[10px] font-semibold uppercase tracking-widest text-navy">Lesson</h4>
          <article className="prose prose-sm max-w-none whitespace-pre-wrap text-sm text-foreground">
            {m.lesson_md || m.body_md}
          </article>

          {m.notes_url && (
            <div className="mt-3 flex flex-wrap gap-2">
              <a
                href={m.notes_url}
                target="_blank"
                rel="noopener"
                className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-navy/30 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
              >
                <Download className="h-3 w-3" aria-hidden="true" /> Study notes (PDF)
              </a>
              {m.checklist_url && (
                <a
                  href={m.checklist_url}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-emerald-500 bg-emerald-50 px-3 py-1.5 text-xs font-semibold text-emerald-900 hover:bg-emerald-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
                >
                  <Download className="h-3 w-3" aria-hidden="true" /> Checklist (PDF)
                </a>
              )}
            </div>
          )}

          {glossary.length > 0 && (
            <section aria-labelledby={`${panelId}-glossary`} className="mt-4 rounded-md border border-navy/15 bg-white p-3">
              <h4 id={`${panelId}-glossary`} className="text-[10px] font-semibold uppercase tracking-widest text-navy">
                Glossary — key legal terms
              </h4>
              <dl className="mt-2 grid gap-2 sm:grid-cols-2">
                {glossary.map((g, i) => (
                  <div key={i} className="text-sm">
                    <dt className="font-semibold text-navy">{g.term}</dt>
                    <dd className="text-muted-foreground">{g.definition}</dd>
                  </div>
                ))}
              </dl>
            </section>
          )}

          {m.scenario_md && (
            <section aria-labelledby={`${panelId}-scenario`} className="mt-3 rounded-md border border-amber-400 bg-amber-50/60 p-3">
              <h4 id={`${panelId}-scenario`} className="text-[10px] font-semibold uppercase tracking-widest text-amber-900">
                Worked scenario
              </h4>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{m.scenario_md}</p>
            </section>
          )}

          {m.activity_md && (
            <section aria-labelledby={`${panelId}-activity`} className="mt-3 rounded-md border border-sky-400 bg-sky-50/60 p-3">
              <h4 id={`${panelId}-activity`} className="text-[10px] font-semibold uppercase tracking-widest text-sky-900">
                Learner activity
              </h4>
              <p className="mt-1 whitespace-pre-wrap text-sm text-foreground">{m.activity_md}</p>
            </section>
          )}

          {checklist.length > 0 && (
            <section aria-labelledby={`${panelId}-checklist`} className="mt-4 rounded-md border-2 border-emerald-500 bg-emerald-50/60 p-3">
              <h4 id={`${panelId}-checklist`} className="text-[10px] font-semibold uppercase tracking-widest text-emerald-900">
                Summary checklist — what to remember
              </h4>
              <ul className="mt-1.5 space-y-1 text-sm text-foreground">
                {checklist.map((k, i) => (
                  <li key={i} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-3.5 w-3.5 shrink-0 text-emerald-700" aria-hidden="true" />
                    <span>{k}</span>
                  </li>
                ))}
              </ul>
            </section>
          )}

          <section aria-labelledby={`${panelId}-downloads`} className="mt-4 rounded-md border border-navy/20 bg-white p-3">
            <h4 id={`${panelId}-downloads`} className="text-[10px] font-semibold uppercase tracking-widest text-navy">
              Module downloads
            </h4>
            <p className="mt-1 text-xs text-muted-foreground">
              {m.summary
                ? m.summary
                : `Branded PDF of the lesson notes and a printable scenario exercise for Module ${m.order_index}. Content is generated live from this module's lesson, key points, scenario and activity.`}
            </p>
            <div className="mt-2 flex flex-wrap gap-2">
              <a
                href={`/api/public/academy-notes/${encodeURIComponent(slug)}/${m.id}`}
                target="_blank"
                rel="noopener"
                className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-navy/30 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-navy/5 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
              >
                <Download className="h-3 w-3" aria-hidden="true" /> Notes PDF
              </a>
              {(m.scenario_md || m.activity_md) && (
                <a
                  href={`/api/public/academy-scenario/${encodeURIComponent(slug)}/${m.id}`}
                  target="_blank"
                  rel="noopener"
                  className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-amber-500 bg-amber-50 px-3 py-1.5 text-xs font-semibold text-amber-900 hover:bg-amber-100 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
                >
                  <Download className="h-3 w-3" aria-hidden="true" /> Scenario exercise PDF
                </a>
              )}
              <button
                type="button"
                onClick={() => {
                  const lines: string[] = [];
                  lines.push(`# ${m.title}`, "");
                  lines.push(m.body_md ?? "", "");
                  if (checklist.length) {
                    lines.push("## Summary checklist", "");
                    for (const k of checklist) lines.push(`- ${k}`);
                    lines.push("");
                  }
                  if (m.scenario_md) lines.push("## Worked scenario", "", m.scenario_md, "");
                  if (m.activity_md) lines.push("## Learner activity", "", m.activity_md, "");
                  const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
                  const url = URL.createObjectURL(blob);
                  const a = document.createElement("a");
                  a.href = url;
                  a.download = `${m.title.replace(/[^a-z0-9]+/gi, "-").toLowerCase()}-notes.md`;
                  document.body.appendChild(a);
                  a.click();
                  a.remove();
                  URL.revokeObjectURL(url);
                }}
                className="inline-flex min-h-11 items-center gap-1.5 rounded-md border border-input bg-white px-3 py-1.5 text-xs font-semibold text-foreground hover:bg-muted focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-navy"
              >
                <Download className="h-3 w-3" aria-hidden="true" /> Markdown
              </button>
            </div>
          </section>

          <div className="mt-5">
            <AccessibleQuiz
              questions={shuffled}
              namePrefix={m.id}
              headingText={`Module quiz · ${shuffled.length} questions · pass 60% · questions randomised`}
              helperText="Select one answer per question. All questions are required."
              submitLabel="Submit quiz"
              onSubmit={onSubmit}
              onResult={(r) => {
                if (!r.passed) {
                  // Re-shuffle for the next retake
                  setShuffled(shuffleArr<Q>(questions));
                }
              }}
            />
            {lastAttempt && (
              <p className="mt-2 text-xs text-muted-foreground">
                Last attempt: {lastAttempt.score}/{lastAttempt.max_score} ({Math.round((lastAttempt.score / lastAttempt.max_score) * 100)}%) — {lastAttempt.passed ? "passed" : "not yet"}
              </p>
            )}
          </div>
        </div>
      )}
    </li>
  );
}


function FinalExamBlock({
  slug, unlocked, alreadyPassed, questions, attempts, onSubmit,
}: any) {
  const [shuffled, setShuffled] = useState<Q[]>(() => shuffleArr<Q>(questions));
  const lastAttempt = attempts[0];

  return (
    <section aria-labelledby="final-exam-heading" className="rounded-lg border-2 border-gold/40 bg-amber-50/40 p-4">
      <div className="flex items-center gap-2">
        <FileQuestion className="h-5 w-5 text-gold" aria-hidden="true" />
        <h3 id="final-exam-heading" className="font-display text-lg text-navy">Final exam</h3>
      </div>
      <p className="mt-1 text-xs text-muted-foreground">
        {shuffled.length} questions · pass 60% · questions randomised. Available once every module quiz is passed.
      </p>

      {!unlocked && (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-muted px-3 py-1.5 text-xs">
          <Lock className="h-3 w-3" aria-hidden="true" /> Pass all module quizzes to unlock.
        </p>
      )}

      {unlocked && !alreadyPassed && (
        <div className="mt-4">
          <AccessibleQuiz
            questions={shuffled}
            namePrefix="final"
            headingText={`Final exam · ${shuffled.length} questions · pass 60% · questions randomised`}
            helperText="Select one answer per question. All questions are required."
            submitLabel="Submit final exam"
            variant="final"
            onSubmit={onSubmit}
            onResult={(r) => {
              if (!r.passed) setShuffled(shuffleArr<Q>(questions));
            }}
          />
          {lastAttempt && (
            <p className="mt-2 text-xs text-muted-foreground">
              Last attempt: {lastAttempt.score}/{lastAttempt.max_score} ({Math.round((lastAttempt.score / lastAttempt.max_score) * 100)}%) — {lastAttempt.passed ? "passed" : "not yet"}
            </p>
          )}
        </div>
      )}

      {alreadyPassed && (
        <p className="mt-3 inline-flex items-center gap-1.5 rounded-md bg-emerald-100 px-3 py-1.5 text-xs text-emerald-900">
          <CheckCircle2 className="h-3 w-3" aria-hidden="true" /> Final exam passed — awaiting admin review.
        </p>
      )}
    </section>
  );
}


const TRACK_LABEL_ACADEMY = "NDLCS Academy";

function IssuedCertificateCard({ courseCode, learnerName }: { courseCode: string; learnerName: string }) {
  const fetchCerts = useServerFn(getMyCertificates);
  const generateServerCert = useServerFn(generateFinalCertificatePdf);
  const { data, isLoading } = useQuery({
    queryKey: ["my-certificates"],
    queryFn: () => fetchCerts(),
  });
  const cert = (data?.certificates ?? []).find((c: any) => c.track_ref === courseCode && !c.revoked_at);
  const [busy, setBusy] = useState(false);

  // Server-side final certificate: signed, hash-embedded, audit-logged.
  async function handleDownload() {
    if (!cert) return;
    setBusy(true);
    try {
      const r = await generateServerCert({ data: { id: cert.id } });
      downloadBase64File(r.base64, r.filename);
      toast.success(`Downloaded. Tamper-evident SHA-256: ${r.documentHash.slice(0, 16)}…`);
    } catch (err: any) {
      // Fall back to client-side generation if the server route fails
      console.warn("[cert] server generation failed, falling back", err);
      try {
        await downloadCertificatePdf({
          verificationCode: cert.verification_code,
          recipientName: cert.recipient_name || learnerName,
          trackLabel: TRACK_LABEL_ACADEMY,
          title: cert.title,
          issuedAt: cert.issued_at,
          trackRef: cert.track_ref,
          signature: cert.signature,
          certificateId: cert.id,
        });
      } catch (err2: any) {
        toast.error(err2?.message ?? "Could not download certificate");
      }
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="rounded-lg border-2 border-emerald-300 bg-emerald-50/60 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <p className="flex items-center gap-2 text-sm font-semibold text-emerald-900">
            <Award className="h-4 w-4" /> Certificate issued
          </p>
          <p className="mt-1 text-xs text-emerald-900/80">
            Your tamper-evident certificate has been signed by an NDLCS administrator. Download the PDF below — every copy includes a QR code anyone can scan to verify it.
          </p>
          {cert && (
            <p className="mt-1 font-mono text-[11px] text-emerald-900/70">
              {cert.verification_code} · issued {new Date(cert.issued_at).toLocaleDateString("en-ZA")}
            </p>
          )}
        </div>
        <div className="flex gap-2">
          <button
            onClick={handleDownload}
            disabled={busy || isLoading || !cert}
            className="inline-flex items-center gap-2 rounded-md bg-navy px-3 py-2 text-xs font-semibold text-navy-foreground hover:bg-navy/90 disabled:opacity-60"
          >
            {busy || isLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Download className="h-3.5 w-3.5" />}
            {cert ? "Download certificate" : "Preparing…"}
          </button>
          <Link
            to="/portal/certificates"
            className="inline-flex items-center gap-1 rounded-md border border-navy px-3 py-2 text-xs font-semibold text-navy hover:bg-navy hover:text-navy-foreground"
          >
            All certificates
          </Link>
        </div>
      </div>
    </div>
  );
}

